import { Accordion, Anchor, Box, Text } from '@zillow/constellation-10';

import type React from 'react';

export type DisclaimerItem = {
  header?: string;
  description?: Array<{
    text: string;
    url?: string;
    stylings?: Array<string>;
  }>;
};

type ExpandableDisclaimerProps = {
  title: string;
  disclaimerContent: Array<DisclaimerItem> | DisclaimerItem | undefined;
  fontType?: string;
  onClick?: () => void;
};

function formatDisclaimerContent(styledTextList: DisclaimerItem['description'], fontType: string): React.ReactNode {
  if (!styledTextList?.length) {
    return null;
  }

  return (
    <>
      {styledTextList.map((styledText, i) => {
        const key = `styled-text-${i}`;
        const content = styledText.url ? (
          <Anchor target="_blank" rel="noopener noreferrer" href={styledText.url}>
            {styledText.text}
          </Anchor>
        ) : (
          styledText.text
        );
        const isEmphasized = styledText.stylings?.includes('emphasize');
        return (
          <Text
            key={key}
            textStyle={fontType === 'legal' ? 'body-xs' : 'body'}
            color="text.subtle"
            css={{
              whiteSpace: 'pre-line',
              ...(isEmphasized ? { fontWeight: 'bold' } : {}),
            }}
          >
            {content}
          </Text>
        );
      })}
    </>
  );
}

const ExpandableDisclaimer = ({ title, disclaimerContent, fontType = 'legal', onClick }: ExpandableDisclaimerProps) => {
  // Handle both array format and single object format
  const disclaimers = Array.isArray(disclaimerContent)
    ? disclaimerContent
    : disclaimerContent
      ? [disclaimerContent]
      : [];

  return (
    <Accordion.Root defaultExpanded={[]} onClick={onClick} data-testid="disclaimer-accordion">
      <Accordion.Item value="disclaimer">
        <Accordion.Header data-test-id="disclaimer-expanded-header" data-testid="disclaimer-toggle">
          <Text textStyle="body-bold">{title}</Text>
        </Accordion.Header>
        <Accordion.Panel data-test-id="disclaimer-expanded-content">
          {disclaimers.map((disclaimer, idx) => (
            <Box key={`disclaimer-${idx}`} css={{ marginBottom: '400' }}>
              <Text textStyle="body-xs" css={{ display: 'block', marginBottom: '200', textDecoration: 'underline' }}>
                {disclaimer.header}
              </Text>
              <Box data-testid={`disclaimer-expanded-content-${idx}`}>
                {formatDisclaimerContent(disclaimer.description, fontType)}
              </Box>
            </Box>
          ))}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion.Root>
  );
};

export default ExpandableDisclaimer;
